import { useState, useCallback } from "react";
import { useMessageApi } from "../../../shared/providers/MessageProvider";
import {
  getAllUsers,
  deleteUser,
  updateUserStatusByEmail,
  updateUserRoleByEmail,
} from "../../../services/userService";
import {
  ITEMS_PER_PAGE,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
} from "../constants/userConstants";
import { updateUserInList } from "../utils/userHelpers";
import { canAssignRole } from "../../../utils/roleHelpers";

/**
 * Custom hook for user management operations
 * @param {Object} userInfo - Current user information
 * @param {boolean} restrictToOwnCompany - Whether to restrict to user's own company
 * @param {string} sortOrder - Sort order: 'asc' or 'desc'
 * @returns {Object} User management state and functions
 */
export const useUserManagement = (userInfo = null, restrictToOwnCompany = false, sortOrder = 'asc') => {
  const message = useMessageApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});

  /**
   * Loads users with pagination and filters
   * @param {boolean} resetData - If true, resets to page 1
   * @param {Object} filterOverride - Optional filters to override current filters
   * @returns {Promise<void>}
   */
  const loadUsers = useCallback(
    async (resetData = false, filterOverride = null) => {
      // Set appropriate loading state
      if (resetData) {
        setLoading(true);
        setUsers([]);
        setPage(1);
        setHasMore(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const currentPage = resetData ? 1 : page;
        const activeFilters =
          filterOverride !== null ? filterOverride : filters;

        // Apply company filter if user is restricted to their own company
        const finalFilters = { ...activeFilters };
        if (restrictToOwnCompany && userInfo?.company) {
          finalFilters.company = userInfo.company;
        }

        // Exclude current logged-in user from results
        if (userInfo?.id) {
          finalFilters.exclude_user_id = userInfo.id;
          finalFilters.current_user_id = userInfo.id;
        } else if (userInfo?.email) {
          finalFilters.exclude_email = userInfo.email;
        }

        if (userInfo?.role_id) {
          finalFilters.current_user_role_id = userInfo.role_id;
        }

        if (userInfo?.company) {
          finalFilters.current_user_company = userInfo.company;
        }

        const result = await getAllUsers({
          page: currentPage,
          limit: ITEMS_PER_PAGE,
          sortOrder: sortOrder,
          ...finalFilters,
        });

        if (result && result.users) {
          const newUsers = result.users;
          const total = result.total || result.count || 0;

          setTotalCount(total);

          if (resetData) {
            // Initial load or reset - replace users
            setUsers(newUsers);
            setPage(2);
            setHasMore(newUsers.length < total);
          } else {
            // Loading more - append users
            setUsers((prev) => {
              const loadedCount = prev.length + newUsers.length;
              setHasMore(loadedCount < total);
              return [...prev, ...newUsers];
            });
            setPage((prev) => prev + 1);
          }
        } else {
          if (resetData) {
            setUsers([]);
            setTotalCount(0);
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Error loading users:", error);
        message.error(ERROR_MESSAGES.LOAD_USERS_FAILED);

        if (resetData) {
          setUsers([]);
          setTotalCount(0);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [page, filters, restrictToOwnCompany, userInfo, sortOrder]
  );

  /**
   * Loads more users for infinite scroll
   * @param {boolean} shouldScroll - If true, scrolls to bottom after loading (handled by component)
   * @returns {Promise<void>}
   */
  const loadMoreUsers = useCallback(async (shouldScroll = false) => {
    if (isLoadingMore || !hasMore || loading) {
      return;
    }
    await loadUsers(false);
    // Scroll is handled by the component (Table.jsx pattern)
  }, [isLoadingMore, hasMore, loading, loadUsers]);

  /**
   * Refreshes the user list (reloads from page 1)
   * @returns {Promise<void>}
   */
  const refreshUsers = useCallback(async () => {
    await loadUsers(true);
  }, [loadUsers]);

  /**
   * Toggles user status (active/inactive)
   * @param {string} userEmail - User email
   * @param {boolean} newStatus - New status value
   * @returns {Promise<boolean>} Success status
   */
  const toggleUserStatus = useCallback(async (userEmail, newStatus) => {
    try {
      const currentUserId = userInfo?.id || null;
      const currentUserRoleId = userInfo?.role_id || null;
      const updatedUser = await updateUserStatusByEmail(userEmail, newStatus, currentUserId, currentUserRoleId);

      if (updatedUser) {
        // Update local state immediately for better UX
        setUsers((prevUsers) =>
          updateUserInList(prevUsers, userEmail, { is_active: newStatus })
        );

        message.success(
          newStatus
            ? SUCCESS_MESSAGES.USER_ACTIVATED
            : SUCCESS_MESSAGES.USER_DEACTIVATED
        );

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error toggling user status:", error);
      message.error(error.message || ERROR_MESSAGES.UPDATE_STATUS_FAILED);
      return false;
    }
  }, [userInfo]);

  /**
   * Changes user role
   * @param {string} userEmail - User email
   * @param {number} newRoleId - New role ID
   * @param {Array} roles - Array of available roles to find role name
   * @returns {Promise<boolean>} Success status
   */
  const changeUserRole = useCallback(
    async (userEmail, newRoleId, roles = []) => {
      try {
        const currentUserId = userInfo?.id || null;
        const currentUserRoleId = userInfo?.role_id || null;
        const numericRoleId = Number(newRoleId);

        // Pass roles array to enable dynamic order-based hierarchy check
        if (!canAssignRole(userInfo, numericRoleId, roles)) {
          message.error("You don't have permission to assign the selected role");
          return false;
        }

        const updatedUser = await updateUserRoleByEmail(userEmail, newRoleId, currentUserId, currentUserRoleId);

        if (updatedUser) {
          // Find the role name from roles array (ensure type-safe comparison)
          const roleData = roles.find(
            (r) => Number(r.id) === numericRoleId
          );
          const roleName = roleData ? roleData.name : "Unknown";

          // Update local state with both role_id and role name for consistency
          setUsers((prevUsers) =>
            updateUserInList(prevUsers, userEmail, {
              role_id: Number(newRoleId),
              role: roleName,
            })
          );

          message.success(SUCCESS_MESSAGES.ROLE_CHANGED(roleName));

          return true;
        }
        return false;
      } catch (error) {
        console.error("Error changing user role:", error);
        message.error(error.message || ERROR_MESSAGES.UPDATE_ROLE_FAILED);
        return false;
      }
    },
    [userInfo, message]
  );

  /**
   * Permanently deletes a user from Auth0 and database
   * @param {number} userId - User ID to delete
   * @returns {Promise<boolean>} Success status
   */
  const removeUser = useCallback(
    async (userId) => {
      try {
        // Single user deletion
        const result = await deleteUser(userId, userInfo?.id || null);

        if (result === true) {
          message.success("User permanently removed from system");
          // Reload from beginning after deletion
          await loadUsers(true);
          return true;
        } else if (result?.success === true && result.warning) {
          message.warning(result.warning);
          // Reload from beginning after deletion
          await loadUsers(true);
          return true;
        } else {
          message.error(ERROR_MESSAGES.DELETE_USER_FAILED);
          return false;
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        message.error(ERROR_MESSAGES.DELETE_USER_FAILED);
        return false;
      }
    },
    [loadUsers, message, userInfo]
  );

  /**
   * Updates filters and reloads data from backend
   * @param {Object} newFilters - New filter values
   * @returns {Promise<void>}
   */
  const updateFilters = useCallback(
    async (newFilters) => {
      setFilters(newFilters);
      // Reload with new filters
      await loadUsers(true, newFilters);
    },
    [loadUsers]
  );

  return {
    users,
    loading,
    isLoadingMore,
    hasMore,
    totalCount,
    filters,
    loadUsers,
    loadMoreUsers,
    refreshUsers,
    updateFilters,
    toggleUserStatus,
    changeUserRole,
    removeUser,
  };
};

export default useUserManagement;
